<template lang="pug">
  #document-manager-table
    WtTitle(h2)
      span.pr-2 Document Manager

    .mb-5
      .level
        .level-left
          .level-item
            .field.is-grouped.is-grouped-multiline
              .control
                BInput(
                  v-model="searchQuery"
                  placeholder="Search documents..."
                  icon="magnify"
                  type="search"
                )
              .control
                BSelect(v-model="selectedType" placeholder="Filter by type")
                  option(value="") All Types
                  option(v-for="t in types" :key="t" :value="t")
                    | {{ t }}
        .level-right
          .level-item
            WtButton(
              color="primary"
              @click.native="openUploadModal"
            )
              span.ml-2 Upload Document

    BModal(has-modal-card :active.sync="uploadModalOpen")
      DocumentUploadModal(@uploaded="onDocumentUploaded")

    BTable(
      :data="documents"
      :paginated="pagination && pagination.total > pagination.perPage"
      :backend-pagination="true"
      :total="pagination ? pagination.total : 0"
      :per-page="pagination ? pagination.perPage : 20"
      :current-page="pagination ? pagination.current_page : 1"
      @page-change="fetchAction({ page: $event })"
      :loading="loading"
      :striped="true"
      default-sort-direction="desc"
      default-sort="id"
      @sort="onTableSort"
    )
      BTableColumn(field="name" label="Name" sortable v-slot="props")
        .level.is-mobile
          .level-left
            .level-item
              | {{ props.row.name }}
          .level-right(v-if="justUploadedId === props.row.id")
            .level-item
              .tag.is-success.is-light
                BIcon(pack="fas" icon="check" size="is-small")
                span.ml-1 NEW

      BTableColumn(field="type" label="Type" sortable v-slot="props")
        .tag.is-light
          | {{ props.row.type }}

      BTableColumn(field="file_name" label="File" sortable v-slot="props")
        | {{ props.row.file_name }}

      BTableColumn(field="file_size" label="Size" v-slot="props")
        | {{ formatFileSize(props.row.file_size) }}

      BTableColumn(field="created_at" label="Uploaded" sortable v-slot="props")
        | {{ formatDate(props.row.created_at) }}

      BTableColumn(label="Action" v-slot="props")
        WtButton(
          @click.native="copyUrl(props.row)"
        ) COPY URL

      BTableColumn(label="" v-slot="props")
        .is-flex.is-justify-content-flex-end
          span.delete-icon(
            @click="confirmDelete(props.row)"
            title="Delete"
            style="cursor: pointer;"
          )
            WtSvgIcon(icon="delete")

      template(#empty v-if="!loading")
        .content.has-text-centered
          p No documents found
</template>

<script>
import { mapActions } from "vuex";
import { dateUtilityMixin } from "@/mixins/dateUtilityMixin";
import WtTitle from "@@/WtTitle/WtTitle.vue";
import WtButton from "@@/WtButton/WtButton.vue";
import WtSvgIcon from "@@/WtSvgIcon/WtSvgIcon.vue";
import WtEntityTable from "@@/WtEntityTable/WtEntityTable.vue";
import DocumentUploadModal from "./DocumentUploadModal.vue";

export default {
  name: "DocumentManagerTable",
  components: {
    WtTitle,
    WtButton,
    WtSvgIcon,
    WtEntityTable,
    DocumentUploadModal
  },
  mixins: [dateUtilityMixin],
  data() {
    return {
      documents: [],
      pagination: null,
      types: [],
      searchQuery: "",
      selectedType: "",
      loading: false,
      uploadModalOpen: false,
      searchTimeout: null,
      justUploadedId: null
    };
  },
  watch: {
    searchQuery() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.fetchDocuments();
      }, 500);
    },
    selectedType() {
      this.fetchDocuments();
    }
  },
  created() {
    this.fetchDocuments();
    this.fetchDocumentTypes();
  },
  methods: {
    ...mapActions(["snackBar"]),
    async fetchDocuments(page = 1) {
      this.loading = true;
      try {
        const response = await this.$store.dispatch(
          "documents/fetchDocuments",
          {
            query: this.searchQuery,
            type: this.selectedType,
            page,
            perPage: 20
          }
        );
        this.documents = response.data || [];
        this.pagination = response.pagination;
      } catch (err) {
        this.snackBar({
          message: "Failed to load documents",
          type: "is-danger"
        });
      } finally {
        this.loading = false;
      }
    },
    async fetchDocumentTypes() {
      try {
        this.types = await this.$store.dispatch("documents/fetchDocumentTypes");
      } catch (err) {
        console.error("Failed to load document types:", err);
      }
    },
    async fetchAction({ page }) {
      await this.fetchDocuments(page);
    },
    async onTableSort() {
      await this.fetchDocuments(1);
    },
    openUploadModal() {
      this.uploadModalOpen = true;
    },
    onDocumentUploaded(newDoc) {
      this.uploadModalOpen = false;
      if (newDoc && newDoc.id) {
        this.justUploadedId = newDoc.id;
        this.searchQuery = "";
        this.selectedType = "";
        this.fetchDocuments(); // Refresh the list to show the new file

        setTimeout(() => {
          this.justUploadedId = null;
        }, 3000);
      }
    },
    async copyUrl(document) {
      try {
        const url = await this.$store.dispatch(
          "documents/fetchDocumentUrl",
          document.id
        );
        await navigator.clipboard.writeText(url);
        this.snackBar({
          message: "URL copied to clipboard!",
          type: "is-success"
        });
      } catch (err) {
        this.snackBar({
          message: "Failed to copy URL",
          type: "is-danger"
        });
      }
    },
    formatFileSize(bytes) {
      if (!bytes) return "0 B";
      const k = 1024;
      const sizes = ["B", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
    },
    formatDate(dateString) {
      return this.date(dateString, "DATE_FORMAT_SHORT_MONTH").formatted;
    },
    confirmDelete(document) {
      this.$buefy.dialog.confirm({
        title: "Delete Document",
        message: `Are you sure you want to delete <strong>${document.name}</strong>? This cannot be undone.`,
        confirmText: "Delete",
        type: "is-danger",
        hasIcon: true,
        onConfirm: async () => {
          try {
            await this.$store.dispatch("documents/deleteDocument", document.id);
            this.snackBar({ message: "Document deleted.", type: "is-success" });
            this.fetchDocuments();
          } catch (err) {
            this.snackBar({
              message: "Failed to delete document.",
              type: "is-danger"
            });
          }
        }
      });
    }
  }
};
</script>

<style lang="sass" scoped>
.delete-icon
  visibility: hidden
  cursor: pointer
  display: inline-flex
  align-items: center

::v-deep tr:hover .delete-icon
  visibility: visible

@media (max-width: 768px)
  .delete-icon
    visibility: visible
</style>
